/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// Core Libraries
import { Component, OnInit, Input, OnDestroy, EventEmitter, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Wove } from 'aspect.js/';
// Services
import { UserService } from '@support/shared/services/user.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
// Interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { Alert, CustomerNotifications } from '@support/api/proxies/responses/api-2025.interface';
// Constants
import { ACCOUNT_LANDING_MODULE_COMPONENTS } from '@experience/landing/common/constants/account-landing.constants';
import {
  ALERT_CONSTANTS,
  ACCOUNT_LANDING_CONSTANTS,
} from '@experience/landing/common/constants/account-landing.constants';
import { CommonUtilService } from '@support/shared/services/common-util.service';

@Wove({ moduleName: ACCOUNT_LANDING_MODULE_COMPONENTS.ACCOUNT_LANDING_MODULE_NAME })
@Component({
  selector: 'tmo-alerts-section',
  templateUrl: './alerts-section.component.html',
  styleUrls: ['../../../../../../style-sheets/components/page-titles.component.scss'],
})
export class AlertsSectionComponent implements OnInit, OnDestroy {
  /**
   * Stores values of Account Notification
   */
  public alert: Alert[];
  /**
   * Stores values of Customer Notification
   */
  public customerNotifications: CustomerNotifications;
  /**
   * Content data
   */
  @Input() public contentData: ConfigData;
  /**
   * Flag to toggle alerts
   */
  @Input() public showIndicators?: boolean;
  /**
   * Flag to toggle alerts
   */
  @Input() public toggleAlerts: boolean;
  /**
   * Flag to show autoPay status
   */
  @Input() public autoPayStatus?: boolean;
  /**
   * Stores values of showAllAlert
   */
  public showAllAlert: boolean;
  /**
   * Stores default alert limit
   */
  public defaultAlertLimit: number;
  /**
   * Stores max. value of AlertLimit
   */
  public maxAlertLimit: number;
  /**
   * event emitter for number of account notifications
   */
  @Output() public totalAccountNotifications: EventEmitter<number>;
  /**
   * Flag to navigate to session pass page
   */
  @Output() public navigateToSessionPass: EventEmitter<boolean>;

  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Stores values of removed primary number text alert
   */
  public primaryNumberTextRemoved: boolean;

  constructor(
    private objectService: ObjectService,
    private userService: UserService,
    private commonUtilService: CommonUtilService,
    private stateStoreUtilsService: StateStoreUtilsService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.totalAccountNotifications = new EventEmitter<number>();
    this.navigateToSessionPass = new EventEmitter<boolean>();
    this.alert = [];
  }

  public ngOnInit(): void {
    this.getNotifications();
    this.checkSmartPhoneEligibilityAlert();
    this.defaultAlertLimit = this.commonUtilService.isNumeric(
      this.objectService.get(this.contentData, 'numberOfAlertsToShow')
    )
      ? Number(this.contentData.numberOfAlertsToShow)
      : ALERT_CONSTANTS.NUMBER_OF_ALERTS_TO_SHOW;
    this.maxAlertLimit = this.defaultAlertLimit;
    this.showAllAlert = false;
  }

  /**
   * Function to to show all Alert Notifications
   */
  public showAll(): void {
    this.showAllAlert = !this.showAllAlert;
    this.maxAlertLimit = this.showAllAlert ? this.alert.length : this.defaultAlertLimit;
  }

  /**
   * Function to remove Alert Notifications
   * @param index value.
   */
  public removeAlert(index: number): void {
    const removedAlert = this.alert.splice(index, 1);
    this.primaryNumberTextRemoved =
      removedAlert[0].text === this.customerNotifications.primaryNumbertext;
    if (
      this.stateStoreUtilsService.getCustomerAccountAutopayStatusData().alert.length > 0 ||
      this.primaryNumberTextRemoved
    ) {
      this.customerNotifications.alert = this.alert;
      this.customerNotifications.primaryNumbertext = this.primaryNumberTextRemoved
        ? ''
        : this.customerNotifications.primaryNumbertext;
      this.stateStoreUtilsService.setCustomerAccountAutopayStatusData(this.customerNotifications);
    }
    this.totalAccountNotifications.emit(this.alert.length);
  }

  /**
   * Destroys the configuration of Recent Charges Page
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * checks & loads credit eligibilty alert
   * @returns void
   */
  public checkSmartPhoneEligibilityAlert(): void {
    const creditEligibility = this.stateStoreUtilsService.getCreditEligibilty();
    const msisdn = this.stateStoreUtilsService.getMsisdn();
    if (creditEligibility && creditEligibility.overallEligibility) {
      const creditEligibilityAlertText = this.commonUtilService.messageFormatter(
        this.contentData.creditEligibilityAlertText,
        [msisdn]
      );
      const creditEligibilityAlert: Alert = { text: creditEligibilityAlertText };
      this.alert.push(creditEligibilityAlert);
      this.totalAccountNotifications.emit(this.alert.length);
    }
  }
  /**
   * Loads and gets the notifications
   * @returns void
   */
  private getNotifications(): void {
    if (
      this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus().toLocaleUpperCase() ===
      ACCOUNT_LANDING_CONSTANTS.PREPAID
    ) {
      const alertsFromContent = this.contentData.alertsForRedirect
        ? this.contentData.alertsForRedirect.split('|')
        : '';
      let isNavigate = false;
      this.userService
        .getCustomerAccountAutopayStatus()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((customerNotifications: CustomerNotifications) => {
          this.customerNotifications = Object.assign({}, customerNotifications);
          const notificationAlert = Object.assign(
            [],
            this.objectService.get(this.customerNotifications, 'alert') || []
          );
          this.alert = this.alert.concat(notificationAlert);
          if (
            !customerNotifications.primaryNumberIdentified &&
            customerNotifications.primaryNumbertext !== ''
          ) {
            this.alert.unshift({
              text: customerNotifications.primaryNumbertext,
            });
          }
          this.totalAccountNotifications.emit(this.alert.length);
          if (this.alert.length > 0) {
            for (const currAlert of this.alert) {
              isNavigate = alertsFromContent.indexOf(currAlert.name) > -1;
              if (isNavigate) {
                break;
              }
            }
          }
          this.navigateToSessionPass.emit(isNavigate);
        });
    }
  }
}
